import {
  Breadcrumb,
  Button,
  Col,
  Collapse,
  Empty,
  Form,
  Radio,
  Row,
  Space,
  notification,
  Typography,
} from 'antd';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { generatePath, Link, useNavigate, useParams } from 'react-router';

import { PATHS } from '@/shared/config';
import {
  GroupNamesDefaultMessage,
  lifeTimeMonthDefaultMessages,
  ServiceNamesDefaultMessage,
} from '@/shared/config/i18n';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { shouldAskAdditionalInfo } from '@/shared/lib/shouldAskAdditionalData';
import { Layout } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { billingModel, EffectToAddType } from '@/entities/billing';
import { organizationModel } from '@/entities/organization';
import { organizationsModel } from '@/entities/organizations';

import { AdditionalDataForm } from '@/features/additionalDataForm';
import {
  BillingProductAccessGuard,
  useBillingProductAccessGuard,
} from '@/features/billingProductAccessGuard';
import {
  CUSTOM_PRICE_FIELD_NAME,
  CustomPriceField,
  getInvoicePrice,
  normalizeCustomPrice,
} from '@/features/customPrice';

import styles from './AddPackages.module.scss';

const { Title, Text } = Typography;
const { Panel } = Collapse;

type AddPackagesFormType = {
  packageID?: string;
  customPrice?: number | null;
};

const selectNewOrderLoadingState =
  billingModel.selectors.getLoadingStateSelector('newOrder');

export const AddPackages: FC = () => {
  const [isAdditionalFormOpen, setIsAdditionalFormOpen] =
    useState<boolean>(false);

  const { organizationID } = useParams<{ organizationID: string }>();

  const navigate = useNavigate();

  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  const listOrganization = useAppSelector(
    organizationsModel.selectors.selectById(organizationID),
  );
  const canEditInventoryProducts = useAppSelector(
    authModel.selectors.selectCanEditInventoryProducts,
  );
  const allPackages = useAppSelector(
    billingModel.selectors.selectAvailablePackages,
  );
  const packages = canEditInventoryProducts
    ? allPackages
    : allPackages.filter((product) => !product.AvailableForAdminsOnly);
  const newOrderLoadingState = useAppSelector(selectNewOrderLoadingState);

  const {
    ID: accountID,
    BillingZones,
    BillingInformation,
  } = useAppSelector(billingModel.selectors.selectAccountInfo);

  const BillingZone = BillingZones?.at(0);

  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();
  const billingProductAccessState = useBillingProductAccessGuard({
    accessDeniedDescription: formatMessage({
      id: 'newPackagePage.accessDenied.message',
      defaultMessage:
        'You do not have permission to add a package for this organization.',
    }),
    organizationID,
  });
  // TODO [2|l] devtools throw warning that form doesnt connect to any Form component but its connected. Need to solve this issue
  const [form] = Form.useForm<AddPackagesFormType>();

  const selectedPackageID = Form.useWatch('packageID', form);
  const selectedPackage = packages.find(({ SKU }) => SKU === selectedPackageID);

  const onFinish = async (data: AddPackagesFormType) => {
    const targetPackage = packages?.find((pack) => pack.SKU === data.packageID);
    const customPrice = normalizeCustomPrice(data[CUSTOM_PRICE_FIELD_NAME]);

    const effectToAdd: EffectToAddType = {
      case: 'PackageAdd',
      value: {
        Package: targetPackage,
      },
    };

    if (
      shouldAskAdditionalInfo(BillingZone, BillingInformation) &&
      getInvoicePrice(targetPackage?.Price, customPrice) > 0
    ) {
      dispatch(
        billingModel.actions.setOrderToAdd({
          Effect: effectToAdd,
          CustomPrice: customPrice,
        }),
      );

      setIsAdditionalFormOpen(true);
    } else {
      try {
        dispatch(
          billingModel.actions.setLoading({
            loaderKey: 'newOrder',
            loaderState: 'pending',
          }),
        );

        await dispatch(
          billingModel.thunks.createOrder({
            AccountID: accountID,
            Effect: effectToAdd,
            CustomPrice: customPrice,
          }),
        ).unwrap();

        navigate(generatePath(PATHS.organization, { organizationID }));
      } catch (error) {
        notification.error({
          message:
            error instanceof Error
              ? error.message
              : formatMessage({
                  id: 'global.network.error',
                  defaultMessage: 'Network error',
                }),
        });

        dispatch(
          billingModel.actions.setLoading({
            loaderKey: 'newOrder',
            loaderState: 'failed',
          }),
        );
      } finally {
        dispatch(
          billingModel.actions.setLoading({
            loaderKey: 'newOrder',
            loaderState: 'idle',
          }),
        );
      }
    }
  };

  useEffect(() => {
    const getProducts = async () => {
      const { AvailableProducts } = await dispatch(
        billingModel.thunks.getAvailableProducts({
          Condition: { case: 'ByAccountID', value: accountID },
        }),
      ).unwrap();

      dispatch(billingModel.actions.setAvailableProducts(AvailableProducts));
    };

    if (accountID) {
      getProducts();
    }
  }, [accountID]);

  if (billingProductAccessState !== 'allowed') {
    return <BillingProductAccessGuard state={billingProductAccessState} />;
  }

  return (
    <Layout.Content>
      <Breadcrumb
        className="p2"
        style={{ padding: '24px 0' }}
        items={[
          {
            title: (
              <Link className={styles.breadcrumb} to={generatePath(PATHS.main)}>
                <FormattedMessage id="breadcrumb.home" defaultMessage="Home" />
              </Link>
            ),
          },
          {
            title: (
              <Link
                className={styles.breadcrumb}
                to={generatePath(PATHS.organization, { organizationID })}
              >
                {organization?.Name ?? listOrganization?.Name ?? ''}
              </Link>
            ),
          },
          {
            title: (
              <FormattedMessage
                id="newPackagePage.title"
                defaultMessage="New package"
              />
            ),
          },
        ]}
      />

      <Form
        form={form}
        name="add-package"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row gutter={32}>
          <Col span={12}>
            <Title level={4}>
              <FormattedMessage
                id="packages.stepOne"
                defaultMessage="Select package"
              />
            </Title>

            <Form.Item
              name="packageID"
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'forms.required',
                    defaultMessage: 'Field is required',
                  }),
                },
              ]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  {packages.map(
                    ({
                      SKU,
                      Name,
                      Price,
                      Currency,
                      Services,
                      Kind: packageKind,
                    }) => (
                      <Radio key={SKU} value={SKU}>
                        <Space direction="horizontal">
                          {Services?.length > 0 ? (
                            <Collapse expandIcon={() => null} size="small">
                              <Panel
                                key={SKU}
                                header={
                                  <Title style={{ marginBottom: 0 }} level={5}>
                                    <>
                                      <Text>{Name}</Text>
                                      {' | '}
                                      <FormattedNumber
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={2}
                                        value={Number(Price ?? '0')}
                                        // eslint-disable-next-line react/style-prop-object
                                        style="currency"
                                        currencyDisplay="narrowSymbol"
                                        currency={Currency}
                                      />
                                      {' | '}

                                      {packageKind.case === 'Package' &&
                                        (packageKind.value.LifeTimeDays > 0
                                          ? formatMessage(
                                              {
                                                id: 'product.lifetime.days',
                                                defaultMessage:
                                                  '{days, plural, =0 {0 days} one {One day} other {{days} days}}',
                                              },
                                              {
                                                days: packageKind.value
                                                  .LifeTimeDays,
                                              },
                                            )
                                          : formatMessage(
                                              lifeTimeMonthDefaultMessages[
                                                packageKind.value.LifeTimeMonths
                                              ],
                                            ))}
                                    </>
                                  </Title>
                                }
                              >
                                <ul>
                                  {Services?.map(({ Types, Group, Kind }) => {
                                    const amount =
                                      Kind.case === 'Countable'
                                        ? Number(Kind.value.Limit)
                                        : 0;

                                    return (
                                      <li key={`${Types.toString()}`}>
                                        {Types.length > 1
                                          ? formatMessage(
                                              GroupNamesDefaultMessage[Group],
                                              {
                                                amount,
                                              },
                                            )
                                          : formatMessage(
                                              ServiceNamesDefaultMessage[
                                                Types.at(0)
                                              ],
                                              {
                                                amount,
                                              },
                                            )}
                                        {Types.length > 1 && (
                                          <ul>
                                            {Types.map((type) => (
                                              <li>
                                                {formatMessage(
                                                  ServiceNamesDefaultMessage[
                                                    type
                                                  ],
                                                  { amount: 0 },
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </Panel>
                            </Collapse>
                          ) : (
                            <Title style={{ marginBottom: 0 }} level={5}>
                              <>
                                <Text>{Name}</Text>
                                {' | '}
                                <FormattedNumber
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={2}
                                  value={Number(Price ?? '0')}
                                  // eslint-disable-next-line react/style-prop-object
                                  style="currency"
                                  currencyDisplay="narrowSymbol"
                                  currency={Currency}
                                />
                                {' | '}

                                {packageKind.case === 'Package' &&
                                  (packageKind.value.LifeTimeDays > 0
                                    ? formatMessage(
                                        {
                                          id: 'product.lifetime.days',
                                          defaultMessage:
                                            '{days, plural, =0 {0 days} one {One day} other {{days} days}}',
                                        },
                                        {
                                          days: packageKind.value.LifeTimeDays,
                                        },
                                      )
                                    : formatMessage(
                                        lifeTimeMonthDefaultMessages[
                                          packageKind.value.LifeTimeMonths
                                        ],
                                      ))}
                              </>
                            </Title>
                          )}
                        </Space>
                      </Radio>
                    ),
                  )}
                </Space>
              </Radio.Group>
            </Form.Item>

            <CustomPriceField
              listPrice={selectedPackage?.Price}
              currency={selectedPackage?.Currency}
            />
          </Col>
        </Row>

        {packages.length > 0 ? (
          <Button
            type="primary"
            htmlType="submit"
            loading={newOrderLoadingState === 'pending'}
          >
            <span>
              <FormattedMessage
                id="newPackagePage.addNewPackage"
                defaultMessage="Add new package"
              />
            </span>
          </Button>
        ) : (
          <Empty
            description={
              <FormattedMessage
                id="packages.noPackages"
                defaultMessage="No packages"
              />
            }
          />
        )}
      </Form>

      <AdditionalDataForm
        isOpen={isAdditionalFormOpen}
        onCloseHandler={() => setIsAdditionalFormOpen(false)}
      />
    </Layout.Content>
  );
};
